import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Megaphone, 
  Sparkles,
  Twitter,
  Linkedin,
  Facebook,
  Instagram,
  Copy,
  Check,
  Loader2,
  Clock,
  Trash2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MarketingStudio = () => {
  const { user } = useAuth();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(null);
  
  const [formData, setFormData] = useState({
    platform: 'linkedin',
    topic: '',
    tone: 'professional'
  });

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', icon: Linkedin, color: 'text-blue-600' },
    { id: 'twitter', name: 'Twitter/X', icon: Twitter, color: 'text-sky-500' },
    { id: 'facebook', name: 'Facebook', icon: Facebook, color: 'text-blue-700' },
    { id: 'instagram', name: 'Instagram', icon: Instagram, color: 'text-pink-600' }
  ];

  const tones = [
    { id: 'professional', name: 'Professional' },
    { id: 'casual', name: 'Casual & Friendly' },
    { id: 'urgent', name: 'Urgent & Action-Oriented' }
  ];

  const topicSuggestions = [
    "Workplace safety tips for summer",
    "New OSHA regulations update",
    "Fire safety awareness month",
    "PPE importance in manufacturing",
    "Employee training success stories"
  ];

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await axios.get(`${API}/marketing/content`, { withCredentials: true });
      setContent(response.data);
    } catch (error) {
      console.error("Error fetching content:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!formData.topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(`${API}/marketing/generate`, formData, { withCredentials: true });
      setContent([response.data, ...content]);
      toast.success("Content generated successfully!");
      setFormData({ ...formData, topic: '' });
    } catch (error) {
      console.error("Error generating content:", error);
      toast.error(error.response?.data?.detail || "Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(null), 2000);
  };

  const getPlatformIcon = (platform) => {
    const p = platforms.find(pl => pl.id === platform);
    if (p) {
      const Icon = p.icon;
      return <Icon className={`w-5 h-5 ${p.color}`} />;
    }
    return <Megaphone className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-accent border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 space-y-8" data-testid="marketing-studio">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h1 className="font-heading text-2xl lg:text-3xl font-bold text-primary">
              AI Marketing Studio
            </h1>
          </div>
          <p className="text-muted-foreground">
            Generate engaging social media content for your safety training platform.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Content Generator */}
          <div className="lg:col-span-5">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Generate Content</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleGenerate} className="space-y-6">
                  <div>
                    <Label htmlFor="platform">Platform</Label>
                    <Select 
                      value={formData.platform} 
                      onValueChange={(value) => setFormData({...formData, platform: value})}
                    >
                      <SelectTrigger data-testid="platform-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {platforms.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            <div className="flex items-center gap-2">
                              <p.icon className={`w-4 h-4 ${p.color}`} />
                              {p.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="tone">Tone</Label>
                    <Select 
                      value={formData.tone} 
                      onValueChange={(value) => setFormData({...formData, tone: value})}
                    >
                      <SelectTrigger data-testid="tone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {tones.map((t) => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="topic">Topic</Label>
                    <Textarea
                      id="topic"
                      value={formData.topic}
                      onChange={(e) => setFormData({...formData, topic: e.target.value})}
                      placeholder="What would you like to post about?"
                      className="min-h-[100px]"
                      data-testid="topic-input"
                    />
                    <div className="mt-2 flex flex-wrap gap-2">
                      {topicSuggestions.slice(0, 3).map((suggestion, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setFormData({...formData, topic: suggestion})}
                          className="text-xs px-2 py-1 bg-secondary hover:bg-slate-200 rounded-full transition-colors"
                          data-testid={`topic-suggestion-${i}`}
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={generating}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600"
                    data-testid="generate-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Content
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Generated Content */}
          <div className="lg:col-span-7">
            <h2 className="font-heading text-lg font-semibold text-primary mb-4">
              Generated Content ({content.length})
            </h2>
            
            {content.length > 0 ? (
              <div className="space-y-4">
                {content.map((item) => (
                  <Card key={item.content_id} className="social-preview" data-testid={`content-${item.content_id}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          {getPlatformIcon(item.platform)}
                          <span className="font-medium capitalize">{item.platform}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className="badge-neutral">
                            {item.status}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopy(item.content, item.content_id)}
                            data-testid={`copy-btn-${item.content_id}`}
                          >
                            {copied === item.content_id ? (
                              <Check className="w-4 h-4 text-success" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>

                      <p className="text-slate-700 whitespace-pre-wrap mb-4">
                        {item.content}
                      </p>

                      {item.hashtags?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {item.hashtags.map((tag, i) => (
                            <span key={i} className="text-accent text-sm font-medium">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>
                          {new Date(item.created_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Megaphone className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                  <h3 className="font-heading text-lg font-semibold text-primary mb-2">No Content Yet</h3>
                  <p className="text-muted-foreground">
                    Use the generator to create your first social media post.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default MarketingStudio;
